import { useState, useEffect, useCallback, memo } from "react";
import { Pie } from "react-chartjs-2";
import {
  Chart,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import AnimacionCarga from "../animacion_de_carga";
import BarraSuperior from "../barra_superior";
import BotonSOS from "../sos/boton_sos";
import { consultarSimple } from "../../../functions/crud";
import "bootstrap-icons/font/bootstrap-icons.css";


const RUTA_FIESTA = "fiesta";

const COLOR_SI = "rgb(115, 236, 119)";
const COLOR_NO = "rgb(255, 108, 97)";

const esSuper = () => localStorage.getItem("Permisos") === "SUPER";

const estaVacio = (valor) =>
  valor === null || valor === undefined || valor === "" || valor === "null" || valor === "false" || valor === false;

const aNumero = (valor) => {
  if (estaVacio(valor)) return 0;
  const n = parseFloat(valor);
  return Number.isNaN(n) ? 0 : n;
};

const calcularEstadisticas = (registros) => {
  const c = {
    siIngreso: 0, noIngreso: 0,
    siRegalos: 0, noRegalos: 0,
    siMercados: 0, noMercados: 0,
    siRefrigerios: 0, noRefrigerios: 0,
    mayores: 0, menores: 0,
  };

  registros.forEach((e) => {
    if (estaVacio(e.ingreso_al_evento)) c.noIngreso += 1;
    else c.siIngreso += 1;

    if (!estaVacio(e.hijos_menores_de_12)) {
      if (estaVacio(e.se_entrego_regalos)) c.noRegalos += 1;
      else c.siRegalos += 1;
    }

    if (estaVacio(e.se_entrego_mercados)) c.noMercados += 1;
    else c.siMercados += 1;

    if (estaVacio(e.se_entrego_refrigerios)) c.noRefrigerios += 1;
    else c.siRefrigerios += 1;

    c.mayores += aNumero(e.censo_invitados_mayores);
    c.menores += aNumero(e.censo_invitados_menores);
  });

  return {
    resumen: c,
    ingresos: [
      [`Ingreso al evento - ${c.siIngreso}`, c.siIngreso],
      [`No ha ingresado - ${c.noIngreso}`, c.noIngreso],
    ],
    mercados: [
      [`Se entregaron - ${c.siMercados}`, c.siMercados],
      [`Sin entregar - ${c.noMercados}`, c.noMercados],
    ],
    regalos: [
      [`Se entregaron - ${c.siRegalos}`, c.siRegalos],
      [`Sin entregar - ${c.noRegalos}`, c.noRegalos],
    ],
    invitados: [
      [`Mayores de edad - ${c.mayores}`, c.mayores],
      [`Menores de edad - ${c.menores}`, c.menores],
    ],
    refrigerios: [
      [`Se entregaron - ${c.siRefrigerios}`, c.siRefrigerios],
      [`Sin entregar - ${c.noRefrigerios}`, c.noRefrigerios],
    ],
  };
};

const tieneDatos = (pares) => pares.some(([, valor]) => valor > 0);


const GraficaTorta = ({ pares }) => {
  const etiquetas = pares.map(([etiqueta]) => etiqueta);
  const valores = pares.map(([, valor]) => valor);
  const total = valores.reduce((a, b) => a + b, 0);

  const data = {
    labels: etiquetas,
    datasets: [
      {
        data: valores,
        backgroundColor: [COLOR_SI, COLOR_NO],
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    ],
  };

  const opciones = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 16,
          padding: 14,
          font: { weight: "bold", size: 13 },
          color: "#1a1a2e",
        },
      },
      tooltip: {
        callbacks: {
          label: (contexto) => {
            const valor = contexto.parsed || 0;
            const pct = total ? ((valor / total) * 100).toFixed(1) : "0.0";
            return ` ${valor} (${pct}%)`;
          },
        },
      },
      datalabels: {
        display: (contexto) => contexto.dataset.data[contexto.dataIndex] > 0,
        formatter: (valor) => (total ? `${((valor / total) * 100).toFixed(1)}%` : "0.0%"),
        color: "#1a1a2e",
        backgroundColor: "rgba(255,255,255,0.85)",
        borderRadius: 4,
        padding: { top: 3, bottom: 3, left: 6, right: 6 },
        font: { weight: "bold", size: 12 },
      },
    },
  };

  return (
    <div style={s.graficaWrap}>
      <Pie data={data} options={opciones} />
    </div>
  );
};

const EstadisticaFiesta = memo(() => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [totalColaboradores, setTotalColaboradores] = useState(0);
  const [cargando, setCargando] = useState(true);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const datos = await consultarSimple(RUTA_FIESTA);
      const registros = Object.values(datos || {});
      setTotalColaboradores(registros.length);
      setEstadisticas(calcularEstadisticas(registros));
    } catch (error) {
      console.error("Error al cargar la estadística del evento:", error);
      setEstadisticas(calcularEstadisticas([]));
      setTotalColaboradores(0);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    Chart.register(ArcElement, Tooltip, Legend, ChartDataLabels);
    cargarDatos();
  }, [cargarDatos]);

  if (!esSuper()) {
    return (
      <div style={s.page}>
        <BarraSuperior />
        <div style={s.sinAcceso}>
          <i className="bi bi-shield-lock" style={{ fontSize: 34, color: "#d1d5db" }} />
          <p style={{ marginTop: 12 }}>Esta sección está disponible únicamente para usuarios SUPER.</p>
        </div>
      </div>
    );
  }

  if (cargando) return <AnimacionCarga />;

  const secciones = [
    { titulo: "1. Ingresos al evento",     datos: estadisticas.ingresos },
    { titulo: "2. Mercados entregados",    datos: estadisticas.mercados },
    { titulo: "3. Regalos entregados",     datos: estadisticas.regalos },
    { titulo: "4. Censo de Invitados",     datos: estadisticas.invitados },
    { titulo: "5. Refrigerios entregados", datos: estadisticas.refrigerios },
  ];

  return (
    <div style={s.page}>
      <BarraSuperior />

      <div style={s.header}>
        <div>
          <h2 style={s.headerTitle}>Estadística del evento</h2>
          <p style={s.headerSub}>Safari con Coordinar — ingresos, entregas e invitados</p>
        </div>
        <button onClick={cargarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
      </div>

      <div style={s.contenedor}>
        {totalColaboradores === 0 ? (
          <div style={s.vacio}>Aún no hay colaboradores registrados en el evento.</div>
        ) : (
          secciones.map((seccion) => (
            <div key={seccion.titulo} style={s.seccion}>
              <h3 style={s.seccionTitulo}>{seccion.titulo}</h3>
              {tieneDatos(seccion.datos) ? (
                <GraficaTorta pares={seccion.datos} />
              ) : (
                <div style={s.seccionVacia}>Sin registros para esta sección.</div>
              )}
            </div>
          ))
        )}
      </div>

      <BotonSOS />
    </div>
  );
});

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 100,
  },
  header: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "28px 24px 20px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  headerTitle: { margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1a1a2e" },
  headerSub: { margin: 0, fontSize: 13, color: "#6b7280" },
  contenedor: { maxWidth: 900, margin: "0 auto", padding: 24 },
  seccion: { marginBottom: 40 },
  seccionTitulo: {
    fontSize: 18,
    fontWeight: 700,
    color: "#000000",
    textAlign: "center",
    margin: "0 0 16px",
  },
  graficaWrap: { height: 320, width: "100%" },
  seccionVacia: { textAlign: "center", color: "#9ca3af", fontSize: 13, padding: 20 },
  vacio: {
    textAlign: "center",
    padding: 60,
    color: "#9ca3af",
    fontSize: 15,
    maxWidth: 420,
    margin: "0 auto",
  },
  sinAcceso: {
    maxWidth: 420,
    margin: "80px auto",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 15,
  },
};

export default EstadisticaFiesta;
